import { decimal } from "./money.js";
/** Matches `openingSetup`'s percentage scale, so the two never disagree in their last digits. */
const PERCENT_SCALE = 36;
/** Matches `openingSetup`'s `inverse`. Truncation here is 45 orders below anything displayed. */
const PRICE_SCALE = 60;
const ZERO = decimal("0");
const ONE = decimal("1");
const HUNDRED = decimal("100");
function price(value) {
    if (value == null || value === "")
        return null;
    let parsed;
    try {
        parsed = decimal(value);
    }
    catch {
        return null;
    }
    return parsed.units > 0n ? parsed : null;
}
/**
 * A percentage may legitimately be negative: a range that does not contain the reference price is
 * legal, and `manualRange` enforces only positivity and order. So this validates the syntax and
 * nothing else — the one bound that matters is checked in `flip`.
 */
function percentValue(value) {
    try {
        return decimal(value);
    }
    catch {
        return null;
    }
}
const negate = (value) => ZERO.sub(value);
const magnitude = (value) => (value.units < 0n ? negate(value) : value);
const invert = (value) => ONE.div(value, PRICE_SCALE);
/** `(p − base) / base`, as a percentage. One division, like `openingSetup`. */
const relative = (value, base) => value.sub(base).mul(HUNDRED).div(base, PERCENT_SCALE);
/**
 * The same bound read in the reciprocal quote. A bound sitting at `s` percent from the entry price
 * is at `−100·s / (100 + s)` percent once both prices are inverted, which is exact arithmetic on
 * the percentage itself — never a second derivation from prices, so the pair the heading prints can
 * never drift from the range it describes. `null` when the denominator vanishes, which means a
 * bound at or below zero price and cannot happen for a real range.
 */
function flip(signedPercent) {
    const denominator = HUNDRED.add(signedPercent);
    if (denominator.units <= 0n)
        return null;
    return negate(signedPercent.mul(HUNDRED)).div(denominator, PERCENT_SCALE);
}
function frame(inverted, 
/** `entry` is already stated in this frame; everything else is canonical and projected here. */
source, requested, mints) {
    const project = (value) => (inverted ? invert(value) : value);
    const side = (canonicalSide) => inverted ? (canonicalSide === "lower" ? "upper" : "lower") : canonicalSide;
    const entry = source.entry;
    const marker = source.marker === null ? null : project(source.marker);
    const bounds = source.bounds
        .map((bound) => ({ ...bound, side: side(bound.side), price: project(bound.price) }))
        .sort((a, b) => a.price.compare(b.price));
    // Inversion reverses order, so the canonical lower bound is this frame's upper one when flipped.
    const range = inverted
        ? { lower: project(source.upper), upper: project(source.lower) }
        : { lower: source.lower, upper: source.upper };
    const marks = bounds.map((bound) => ({
        role: bound.stop ? "auto-exit-stop" : bound.side === "lower" ? "range-start" : "range-end",
        side: bound.side,
        price: bound.price.toString(),
        percent: relative(bound.price, entry).toString(),
        // Only a stop prints a distance, and the label says "away" rather than a direction, so the
        // value is a magnitude. Without a marker price there is no distance to state.
        awayPercent: bound.stop && marker !== null ? magnitude(relative(bound.price, marker)).toString() : null,
    }));
    const beyond = marker === null
        ? null
        : marker.compare(range.lower) < 0
            ? { side: "lower", percent: magnitude(relative(marker, range.lower)).toString() }
            : marker.compare(range.upper) > 0
                ? { side: "upper", percent: magnitude(relative(marker, range.upper)).toString() }
                : null;
    return {
        inverted,
        baseMint: mints.base,
        quoteMint: mints.quote,
        entryPrice: entry.toString(),
        requestedLowerPercent: requested.lower.toString(),
        requestedUpperPercent: requested.upper.toString(),
        marks,
        markerPrice: marker === null ? null : marker.toString(),
        markerPercent: marker === null ? null : relative(marker, entry).toString(),
        beyond,
    };
}
/**
 * Every percentage the range block prints, in both quotes, measured from the entry price.
 *
 * The opening side comes from `OpeningSetup` alone — never from the stored quote — so a re-centred
 * opening, whose percentages come from `recenterRange` rather than from the request, produces a
 * heading and labels that descend from the same two numbers. Returns `null` when the geometry is
 * unavailable or degenerate; a missing trigger is not degenerate, it simply contributes no mark.
 */
export function rangeOrientations(input) {
    const reference = price(input.opening.referencePrice);
    const lower = price(input.executedLower);
    const upper = price(input.executedUpper);
    if (!reference || !lower || !upper || upper.compare(lower) <= 0)
        return null;
    if (!input.tokenAMint || !input.tokenBMint)
        return null;
    const below = percentValue(input.opening.belowPercent);
    const above = percentValue(input.opening.abovePercent);
    if (!below || !above)
        return null;
    // `belowPercent`/`abovePercent` are magnitudes away from the entry price: below the lower bound,
    // above the upper one. As signed offsets in the opening frame that is `−below` and `+above`.
    const openingLower = negate(below);
    const openingUpper = above;
    const otherLower = flip(openingUpper);
    const otherUpper = flip(openingLower);
    if (!otherLower || !otherUpper)
        return null;
    const lowerTrigger = price(input.lowerTrigger);
    const upperTrigger = price(input.upperTrigger);
    const bounds = [
        { side: "lower", price: lower, stop: false },
        { side: "upper", price: upper, stop: false },
        ...(lowerTrigger ? [{ side: "lower", price: lowerTrigger, stop: true }] : []),
        ...(upperTrigger ? [{ side: "upper", price: upperTrigger, stop: true }] : []),
    ];
    // The opening frame keeps `referencePrice` verbatim; only the other frame pays one inversion, so
    // no value is ever the reciprocal of a reciprocal.
    const canonicalEntry = input.opening.inverted ? invert(reference) : reference;
    const invertedEntry = input.opening.inverted ? reference : invert(reference);
    const marker = price(input.markerPrice);
    const source = { lower, upper, bounds, marker };
    const openingPair = { lower: openingLower, upper: openingUpper };
    const otherPair = { lower: otherLower, upper: otherUpper };
    const canonicalFrame = frame(false, { ...source, entry: canonicalEntry }, input.opening.inverted ? otherPair : openingPair, { base: input.tokenAMint, quote: input.tokenBMint });
    const invertedFrame = frame(true, { ...source, entry: invertedEntry }, input.opening.inverted ? openingPair : otherPair, { base: input.tokenBMint, quote: input.tokenAMint });
    return input.opening.inverted
        ? { opening: invertedFrame, flipped: canonicalFrame }
        : { opening: canonicalFrame, flipped: invertedFrame };
}
